// src/components/AboutChat.jsx
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Form, Button, Spinner } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';
import { trackChatInteraction, trackSectionView } from '../utils/analytics';

export default function AboutChat() {
  const { t } = useTranslation();
  // Inicializa o histórico vazio (persona controlada pelo n8n)
  const [messages, setMessages] = useState([]);
  // Gera um ID de sessão único para manter o contexto no n8n
  const [sessionId] = useState(() => 'session-' + Math.random().toString(36).substr(2, 9));

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);

  // Sugestões de perguntas
  const suggestions = t('chat.suggestionsList');

  // Rastrear visualização da seção do chat
  useEffect(() => {
    trackSectionView('Chat');
  }, []);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);

    // Adiciona a mensagem do usuário ao histórico
    const newMessage = { role: 'user', content: input };
    let updated = [...messages, newMessage];

    // Limita histórico para no máximo 20 mensagens
    if (updated.length > 20) {
      updated = updated.slice(updated.length - 20);
    }

    setMessages(updated);
    setInput('');

    // Rastrear envio de mensagem
    trackChatInteraction('message_sent', input.substring(0, 50));

    try {
      // Envia para o endpoint da API
      const { data } = await axios.post('/api/chat', {
        messages: updated,
        sessionId: sessionId
      });
      const reply = String(data?.reply || 'Resposta vazia');
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      // Erro silencioso
      const errorMsg = String(err.response?.data?.error || err.message || 'Desculpe, houve um erro. Tente novamente em seguida.');
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const visibleMessages = messages.filter(m => m.role !== 'system');
  const showSuggestions = visibleMessages.length === 0;

  return (
    <section id="chat" className="panel">
      <div className="d-flex align-items-center mb-4">
        <div className="me-3" style={{ fontSize: '2rem' }}>
          <i className="bi bi-robot" style={{ color: 'var(--accent)' }}></i>
        </div>
        <div>
          <h2 className="text-start mb-2">{t('chat.title')}</h2>
          <p
            className="mb-0"
            style={{
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.85)',
              fontFamily: 'var(--font-body)',
              fontWeight: '400',
              lineHeight: '1.7',
              letterSpacing: '0.4px',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
            }}
          >
            {t('chat.description')}
          </p>
        </div>
      </div>

      {/* Área de mensagens */}
      <div
        ref={containerRef}
        className="chat-container mb-3"
        style={{
          height: '400px',
          overflowY: 'auto',
          padding: '1rem',
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '0.5rem',
          border: '1px solid rgba(0, 245, 255, 0.2)'
        }}
      >
        {showSuggestions && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-4"
          >
            <p className="text-light mb-3" style={{ fontSize: '0.95rem' }}>
              <strong><i className="bi bi-lightbulb me-2"></i>{t('chat.suggestions')}</strong>
            </p>
            <div className="d-flex flex-wrap gap-2 justify-content-center">
              {suggestions.map((suggestion, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    setInput(suggestion);
                    // Rastrear clique em sugestão
                    trackChatInteraction('suggestion_clicked', suggestion.substring(0, 50));
                    // Aguarda um momento para o estado atualizar
                    await new Promise(resolve => setTimeout(resolve, 50));
                    // Cria uma mensagem temporária para enviar
                    const tempInput = suggestion;
                    setInput('');
                    setLoading(true);
                    const newMessage = { role: 'user', content: tempInput };
                    let updated = [...messages, newMessage];
                    if (updated.length > 20) {
                      updated = updated.slice(updated.length - 20);
                    }
                    setMessages(updated);
                    try {
                      const { data } = await axios.post('/api/chat', { messages: updated });
                      const reply = String(data?.reply || 'Resposta vazia');
                      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
                    } catch (err) {
                      // Erro silencioso
                      const errorMsg = String(err.response?.data?.error || err.message || 'Desculpe, houve um erro. Tente novamente em seguida.');
                      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="suggestion-btn"
                  style={{
                    background: 'rgba(0, 245, 255, 0.1)',
                    border: '1px solid rgba(0, 245, 255, 0.3)',
                    color: 'var(--accent)',
                    padding: '0.5rem 1rem',
                    borderRadius: '1.5rem',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                >
                  {suggestion}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        <AnimatePresence>
          {visibleMessages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className={`d-flex mb-3 ${m.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
            >
              <div
                className={`message-bubble ${m.role === 'user' ? 'user-message' : 'assistant-message'}`}
                style={{
                  maxWidth: '75%',
                  padding: '0.75rem 1rem',
                  borderRadius: m.role === 'user' ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                  background: m.role === 'user'
                    ? 'linear-gradient(135deg, rgba(0, 245, 255, 0.2), rgba(0, 245, 255, 0.1))'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                  border: `1px solid ${m.role === 'user' ? 'rgba(0, 245, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)'}`,
                  color: 'var(--text-light)',
                  fontSize: '0.95rem',
                  lineHeight: '1.5',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  wordWrap: 'break-word'
                }}
              >
                <div className="d-flex align-items-start">
                  {m.role === 'assistant' && (
                    <i className="bi bi-robot me-2" style={{ fontSize: '1.2rem', color: 'var(--accent)' }} title="Marnet Tech"></i>
                  )}
                  <div style={{ flex: 1 }}>
                    {typeof m.content === 'string' ? (
                      m.content.split('\n').map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < m.content.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))
                    ) : (
                      <span style={{ color: '#ff6b6b' }}>
                        {String(m.content || 'Erro ao processar mensagem')}
                      </span>
                    )}
                  </div>
                  {m.role === 'user' && (
                    <i className="bi bi-person ms-2" style={{ fontSize: '1.2rem', color: 'var(--accent)' }}></i>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="d-flex align-items-start mb-3"
          >
            <div
              className="message-bubble assistant-message"
              style={{
                padding: '0.75rem 1rem',
                borderRadius: '1rem 1rem 1rem 0.25rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <div className="d-flex align-items-center">
                <i className="bi bi-robot me-2" style={{ color: 'var(--accent)' }} title="Marnet Tech"></i>
                <Spinner animation="border" size="sm" className="me-2" />
                <span style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {t('chat.thinking')}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Formulário de input */}
      <Form
        onSubmit={e => {
          e.preventDefault();
          sendMessage();
        }}
        className="d-flex gap-2"
      >
        <Form.Control
          type="text"
          placeholder={t('chat.placeholder')}
          value={input}
          onChange={e => setInput(e.target.value)}
          disabled={loading}
          onKeyPress={e => {
            if (e.key === 'Enter' && !e.shiftKey && !loading) {
              e.preventDefault();
              sendMessage();
            }
          }}
          style={{
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 245, 255, 0.3)',
            color: 'var(--text-light)',
            padding: '0.75rem 1rem',
            fontSize: '1rem',
            fontFamily: 'var(--font-body)'
          }}
          className="flex-grow-1"
        />
        <Button
          variant="primary"
          onClick={(e) => {
            e.preventDefault();
            sendMessage();
          }}
          disabled={loading || !input.trim()}
          style={{
            background: loading || !input.trim()
              ? 'rgba(0, 245, 255, 0.3)'
              : 'linear-gradient(135deg, rgba(0, 245, 255, 0.8), rgba(0, 200, 255, 0.8))',
            border: '1px solid rgba(0, 245, 255, 0.5)',
            padding: '0.75rem 1.5rem',
            minWidth: '100px',
            fontWeight: '500',
            transition: 'all 0.3s'
          }}
          className="d-flex align-items-center justify-content-center"
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              <span>{t('chat.sending')}</span>
            </>
          ) : (
            <>
              <span className="me-2">{t('chat.send')}</span>
              <i className="bi bi-send"></i>
            </>
          )}
        </Button>
      </Form>

      <style>{`
        .chat-container::-webkit-scrollbar {
          width: 8px;
        }
        .chat-container::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }
        .chat-container::-webkit-scrollbar-thumb {
          background: rgba(0, 245, 255, 0.3);
          border-radius: 4px;
        }
        .chat-container::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 245, 255, 0.5);
        }
        .suggestion-btn:hover {
          background: rgba(0, 245, 255, 0.2) !important;
          border-color: rgba(0, 245, 255, 0.5) !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 245, 255, 0.2);
        }
        .message-bubble {
          animation: fadeIn 0.3s ease-in;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .form-control::placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
          font-family: var(--font-body) !important;
          font-size: 1rem !important;
          font-weight: 400 !important;
          opacity: 1 !important;
        }
        .form-control:focus::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }
      `}</style>
    </section>
  );
}
